import axios from 'axios';
import wooCommerceStore from '@/stores/woocommerceStore';

export const buildOrderPayload = ({
  formData,
  selectedPaymentMethod,
  paymentMethods,
  selectedShippingMethod,
  shippingMethods,
  selectedSlot,
  useDifferentShipping,
  cart,
  userId,
  appliedCoupons = [],
}) => {
  return {
    payment_method: selectedPaymentMethod,
    payment_method_title: paymentMethods.find(method => method.id === selectedPaymentMethod)?.title || "",
    billing: formData.billing,
    shipping: useDifferentShipping ? formData.shipping : formData.billing,
    line_items: cart.map((item) => {
      const lineItem = item.type === 'variable' ? {
        product_id: item.parent_id,
        variation_id: item.id,
        quantity: item.quantity,
      } : {
        product_id: item.id,
        quantity: item.quantity,
      };

      // Add note to meta data if it exists
      if (item.note) {
        lineItem.meta_data = [{
          key: '_line_item_note',
          value: item.note,
        }];
      }

      return lineItem;
    }),
    shipping_lines: selectedShippingMethod ? (() => {
      const method = shippingMethods.find(method => method.method_id === selectedShippingMethod);
      return [{
        method_id: selectedShippingMethod,
        method_title: method?.title || method?.method_title || "",
        total: method?.settings?.cost?.value || "0.00",
      }];
    })() : [],
    customer_note: formData?.meta_data?.customer_note || "",
    meta_data: [
      {
        key: "selected_slot",
        value: selectedSlot,
      },
      {
        key: "replacement_preference",
        value: formData?.meta_data?.replacement_preference,
      },
      {
        key: "receiving_preference",
        value: formData?.meta_data?.receiving_preference,
      },
      {
        key: "customer_note",
        value: formData?.meta_data?.customer_note || "",
      },
      // Billing address meta data
      {
        key: "_billing_street",
        value: formData.billing.street || "",
      },
      {
        key: "_billing_house_number",
        value: formData.billing.house_number || "",
      },
      {
        key: "_billing_floor",
        value: formData.billing.floor || "",
      },
      {
        key: "_billing_apartment_number",
        value: formData.billing.apartment_number || "",
      },
      {
        key: "_billing_customer_type",
        value: formData.billing.customer_type || "personal",
      },
      {
        key: "_billing_company_id",
        value: formData.billing.company_id || "",
      },
      {
        key: "_billing_address_type",
        value: formData.billing.address_type || "house",
      },
      {
        key: "_billing_address_notes",
        value: formData.billing.address_notes || "",
      },
      // Shipping address meta data
      {
        key: "_shipping_street",
        value: formData.shipping.street || "",
      },
      {
        key: "_shipping_house_number",
        value: formData.shipping.house_number || "",
      },
      {
        key: "_shipping_floor",
        value: formData.shipping.floor || "",
      },
      {
        key: "_shipping_apartment_number",
        value: formData.shipping.apartment_number || "",
      },
      {
        key: "_shipping_address_type",
        value: formData.shipping.address_type || "house",
      },
      {
        key: "_shipping_address_notes",
        value: formData.shipping.address_notes || "",
      },
    ],
    customer_id: userId || 0,
    coupon_lines: appliedCoupons.map(coupon => ({
      code: coupon.code,
    })),
  };
};

export const saveOrder = async (orderPayload, activeOrderDetails = null) => {
  let response;

  if (activeOrderDetails) {
    // Update existing order
    const parsedOrder = JSON.parse(activeOrderDetails);
    const { id: orderId, shipping_lines, line_items: existingLineItems, coupon_lines: existingCouponLines } = parsedOrder;

    // Find the existing shipping line ID if it exists
    const existingShippingLineId = shipping_lines?.[0]?.id;

    // Create a map of existing items by their unique key
    const existingItemsMap = existingLineItems.reduce((acc, item) => {
      const key = item.variation_id ? `${item.product_id}_${item.variation_id}` : `${item.product_id}`;
      acc[key] = item;
      return acc;
    }, {});

    // Process current cart items
    const updatedLineItems = orderPayload.line_items.map(item => {
      const key = item.variation_id ? `${item.product_id}_${item.variation_id}` : `${item.product_id}`;
      const existingItem = existingItemsMap[key];

      if (existingItem) {
        // Update existing item with new quantity
        return {
          id: existingItem.id,
          product_id: item.product_id,
          variation_id: item.variation_id,
          quantity: item.quantity,
        };
      } else {
        // Add new item
        return {
          product_id: item.product_id,
          variation_id: item.variation_id,
          quantity: item.quantity,
        };
      }
    });

    // Add removed items with quantity 0
    Object.values(existingItemsMap).forEach(item => {
      const key = item.variation_id ? `${item.product_id}_${item.variation_id}` : `${item.product_id}`;
      const stillExists = orderPayload.line_items.some(current => {
        const currentKey = current.variation_id ? `${current.product_id}_${current.variation_id}` : `${current.product_id}`;
        return currentKey === key;
      });

      if (!stillExists) {
        updatedLineItems.push({
          id: item.id,
          product_id: item.product_id,
          variation_id: item.variation_id,
          quantity: 0,
        });
      }
    });

    // Modify the shipping lines to update existing one instead of adding new
    const updatedPayload = {
      ...orderPayload,
      line_items: updatedLineItems,
      shipping_lines: orderPayload.shipping_lines.map(line => ({
        ...line,
        ...(existingShippingLineId ? { id: existingShippingLineId } : {}),
      })),
      coupon_lines: orderPayload.coupon_lines.map(coupon => {
        const existingCoupon = existingCouponLines?.find(line => line.code === coupon.code);
        return {
          ...coupon,
          ...(existingCoupon ? { id: existingCoupon.id } : {}),
        };
      }),
    };

    const finalPayload = {
      orderId,
      ...updatedPayload,
    };

    console.log('Updating order with payload:', {
      orderId,
      line_items: updatedPayload.line_items,
      shipping_lines: updatedPayload.shipping_lines,
    });

    response = await axios.put('/api/shop/order/' + orderId, finalPayload, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  } else {
    // Create new order
    response = await axios.post('/api/shop/order', orderPayload, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  }

  if (response.status === 200 || response.status === 201) {
    localStorage.setItem('activeOrderDetails', JSON.stringify(response.data));
    localStorage.removeItem('saved_address');
  }

  return response;
};

export const registerGuestUser = async (userData) => {
  const response = await axios.post('/api/auth/register', {
    email: userData.email,
    password: userData.password,
    repeat_password: userData.repeat_password,
    first_name: userData.first_name,
    last_name: userData.last_name,
  });

  if (response.data.token && response.data.user) {
    localStorage.setItem('token', response.data.token);
    return response.data.user.id;
  }

  throw new Error('Failed to register guest user');
};

export const restoreLastOrder = async (setIsRestoring) => {
  try {
    setIsRestoring(true);
    const token = localStorage.getItem('token');
    if (!token) return;

    const response = await fetch('/api/shop/order', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const orders = await response.json();
    if (!orders || orders?.orders?.length === 0) return;

    const lastOrder = orders.orders[0]; // Get the most recent order

    // Clear existing cart
    wooCommerceStore.clearCart();

    // Fetch current product data for each line item
    const lineItems = lastOrder?.line_items;
    if (!lineItems || lineItems.length === 0) return;
    for (const item of lineItems) {
      try {
        const response = await fetch(`/api/shop/product/${item.product_id}`);
        const product = await response.json();

        if (item.variation_id) {
          // For variable products, fetch the variation separately
          const variationResponse = await fetch(`/api/shop/product/variation/${item.variation_id}?productId=${item.product_id}`);
          const variation = await variationResponse.json();

          if (variation && variation.purchasable !== false && variation.stock_status === 'instock' && variation.status === 'publish') {
            const variationProduct = {
              ...variation,
              parent_id: item.product_id,
              type: 'variable',
              price: variation.price,
              regular_price: variation.regular_price,
              sale_price: variation.sale_price,
            };
            wooCommerceStore.addToCart(variationProduct, item.quantity);
          }
        } else {
          // For simple products
          if (product && product.purchasable !== false && product.stock_status === 'instock' && product.status === 'publish') {
            wooCommerceStore.addToCart(product, item.quantity);
          }
        }
      } catch (error) {
        console.error(`Error fetching product ${item.product_id}:`, error);
      }
    }

    // Open the cart
    wooCommerceStore.toggleCart();
  } catch (error) {
    console.error('Error restoring last order:', error);
  } finally {
    setIsRestoring(false);
  }
};

export const reorderItems = async (order) => {
  try {
    // Clear existing cart
    wooCommerceStore.clearCart();

    // Fetch current product data for each line item
    const lineItems = order.line_items;
    for (const item of lineItems) {
      try {
        if (!item?.id) {
          continue;
        }

        const response = await fetch(`/api/shop/product/${item.product_id}`);
        const product = await response.json();

        if (item.variation_id) {
          // For variable products, fetch the variation separately
          const variationResponse = await fetch(`/api/shop/product/variation/${item.variation_id}?productId=${item.product_id}`);
          const variation = await variationResponse.json();

          if (variation && variation.purchasable !== false) {
            const variationProduct = {
              ...variation,
              parent_id: item.product_id,
              parent_name: product.name,
              type: 'variable',
              price: variation.price,
              regular_price: variation.regular_price,
              sale_price: variation.sale_price,
            };
            wooCommerceStore.addToCart(variationProduct, item.quantity);
          }
        } else {
          // For simple products
          if (product && product.purchasable !== false) {
            wooCommerceStore.addToCart(product, item.quantity);
          }
        }
      } catch (error) {
        console.error(`Error fetching product ${item.product_id}:`, error);
      }
    }

    // Open the cart
    wooCommerceStore.toggleCart();
  } catch (error) {
    console.error('Error restoring order:', error);
    throw error;
  }
};